import {
    collection,
    collectionGroup,
    getFirestore,
    limit,
    onSnapshot,
    orderBy,
    query,
    where,
    or,
    and,
    QuerySnapshot,
    QueryFilterConstraint,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { HealthProviderId, ID, OrderState, Order, UserOffer } from '../../Models';
import { orderConverter, userOfferConverter } from './converters';
import { app } from '../firebase';
import { fillPatientData } from './crud';

type RecordCallback<T> = (orders: Record<string, T>) => void;

const db = getFirestore(app);
export type SubscriptionReturn = {
    emittedOrders: Order[];
    assignedOrders: Order[];
    approvedOrders: Order[];
};
export const ordersSubscription = (
    healthProviders: HealthProviderId[],
    onLoad: (resp: SubscriptionReturn) => void,
) => {
    const userId = getAuth(app).currentUser?.uid;
    return onSnapshot(
        query(
            collection(db, 'orders'),
            or(
                and(
                    where('state', '==', 'emitted'),
                    where('viewable_by', 'array-contains', userId),
                ),
                and(
                    where('state', 'in', [
                        'assigned',
                        'validated',
                        'approved',
                        'ready_for_pickup',
                        'delivering',
                        'fulfilled', // todo este estado no debería estar en las susbcriptions
                        'cancellation_requested',
                    ] as OrderState[]),
                    where('assigned_pharmacy_id', '==', userId),
                ),
            ),
            orderBy('creation_timestamp', 'desc'),
            limit(150),
        ),
        (querySnapshot) => onNextOrdersSnapshot(querySnapshot, healthProviders, onLoad),
    );
};

const onNextOrdersSnapshot = (
    querySnapshot: QuerySnapshot,
    healthProviders: HealthProviderId[],
    onLoad: (resp: SubscriptionReturn) => void,
) => {
    const emittedOrders: Order[] = [];
    const assignedOrders: Order[] = [];
    const approvedOrders: Order[] = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const docChange of querySnapshot.docChanges()) {
        const order = orderConverter.fromFirestore(docChange.doc);
        if (order.state === 'emitted' && !healthProviders.includes(order.health_provider)) {
            break;
        }
    }
    querySnapshot.forEach((doc) => {
        const order = orderConverter.fromFirestore(doc);
        switch (order.state) {
            case 'emitted':
                // sólo filtramos los emitted, porque puede ocurrir que se cambien los filtros,
                // pero el pedido ya está asignado. En ese caso queremos que la farmacia
                // siga manejando ese pedido
                if (healthProviders.length && !healthProviders.includes(order.health_provider)) {
                    return;
                }
                emittedOrders.push(order);
                break;
            case 'assigned':
            case 'validated':
            case 'cancellation_requested':
                assignedOrders.push(order);
                break;
            case 'approved':
            case 'ready_for_pickup':
            case 'delivering':
            case 'fulfilled':
                approvedOrders.push(order);
                break;
            default:
                break;
        }
    });
    // TODO:
    // todo esto pide la información de los pacientes CADA vez que hay un cambio
    // deberíamos cachear esta info? o firebase ya la cachea?
    fillPatientData([...assignedOrders, ...approvedOrders]).then(() =>
        onLoad({
            emittedOrders,
            assignedOrders,
            approvedOrders,
        }),
    );
};
export const userOffersSubscription = (onLoad: RecordCallback<UserOffer>) =>
    onSnapshot(
        query(
            collectionGroup(db, 'offers'),
            where('state', 'in', ['emitted', 'winner']),
            where('pharmacy_id', '==', getAuth(app).currentUser?.uid),
            orderBy('creation_timestamp', 'desc'),
            limit(500),
        ).withConverter(userOfferConverter),
        (querySnapshot) => {
            const offers: Record<ID, UserOffer> = {};
            querySnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                if (docSnap.exists()) {
                    // puede ocurrir que haya sido eliminada
                    offers[data.order_id] = data;
                }
            });
            onLoad(offers);
        },
    );
